import React, { useEffect, useState } from 'react';
import { useGetInfluencerBySlugQuery } from '~/features/Api/apiSlice';
import { LoadingIndicator } from '~/components/ui/LoadingIndicator';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import Influencer from '~/features/Influencer/Influencer';
import { LiveReplays } from '~/components/live/live-replays/LiveReplays';
import useIsLiveHappeningWithPolling from '~/hooks/useIsLiveHappeningWithPolling';
import { useReplays } from '~/hooks/live/useReplays';
import { useCustomer } from '~/context/CustomerContext';
import useShopInfo from '~/hooks/live/useShopInfo';
import { useAppDispatch } from '~/store/hooks';
import { setInfluencer } from '~/features/Influencer/influencerSlice';
import { useCart } from '~/context/CartContext';
import getStreamIdToPlay from '~/features/Influencer/helpers/getStreamIdToPlay';
import { LoadableLOTWConnector } from '~/components/live/LoadableLOTWConnector';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

const CART_POLLING_INTERVAL = 10000;

interface InfluencerProps {
  slug: string;
  selectedStreamId?: number;
  onSelectReplay: (id: number) => void;
  showRegistrationConfirmation: boolean;
}

const InfluencerContainer: React.FC<InfluencerProps> = ({
  slug,
  selectedStreamId,
  onSelectReplay,
  showRegistrationConfirmation,
}) => {
  const [liveSaleJustEnded, setLiveSaleJustEnded] = useState(false);
  const [latestLiveTime, setLatestLiveTime] = useState<number>();
  const { streamInfo } = useIsLiveHappeningWithPolling();
  const isInfluencerLive = !!(
    streamInfo?.influencerSlug === slug && streamInfo?.is_streaming_now
  );
  const { customer } = useCustomer();
  const isGuest = !!customer?.isGuest;
  const shopInfo = useShopInfo();
  const { name: shopName, appStoreUrls } = shopInfo ?? { appStoreUrls: {} };
  const dispatch = useAppDispatch();
  const { cart, loadCart } = useCart();

  const {
    data: influencer,
    isFetching,
    isError,
    isUninitialized,
  } = useGetInfluencerBySlugQuery(slug);

  useEffect(() => {
    const interval = setInterval(() => {
      loadCart();
    }, CART_POLLING_INTERVAL);

    return () => clearInterval(interval);
  }, [loadCart]);

  useEffect(() => {
    if (!influencer) return;

    dispatch(setInfluencer(influencer));

    return () => {
      dispatch(setInfluencer(null));
    };
  }, [influencer, dispatch]);

  const {
    replays,
    hasMore,
    isLoading: areReplaysLoading,
    isError: hasReplaysError,
    checkForMore,
  } = useReplays({
    influencerSlug: slug,
  });

  useEffect(() => {
    const latest = replays[0];

    if (!latest || !latest.ended_at) return;

    // ended_at omits the milliseconds
    setLatestLiveTime(latest.ended_at * 1000);
  }, [replays]);

  if (isFetching) {
    return <LoadingIndicator />;
  }

  // Due to the way the influencer route is managed on the monolith
  // we should always be able to get the influencer by this slug.
  // This error handling is truly unexpected.
  if (isError || (!isUninitialized && !influencer)) {
    return <p className={'p-4'}>Something went wrong</p>;
  }

  // Should be impossible at this point
  if (!influencer || !customer) return null;

  // This is tricky. We need to know that the current video that is actually
  // playing is indeed a live video, not a replay. But there are a few ways
  // a live video can play. So, this condition checks if the influencer is live,
  // and if the selectedStreamId is the same as the current live stream, OR
  // if there is no selectedStreamId (which means the live is playing anyway).
  const isVideoLive =
    isInfluencerLive &&
    (!selectedStreamId || selectedStreamId === streamInfo?.live_sale_id);

  const handleLiveSaleEnded = () => {
    setLiveSaleJustEnded(true);
  };

  const isStreamChosen = isInfluencerLive || !!selectedStreamId;

  const streamIdToPlay = getStreamIdToPlay({
    liveSaleIdFromStreamInfo: streamInfo?.live_sale_id,
    selectedStreamId,
    isInfluencerLive,
    mostRecentReplay: replays[0],
    nextLive: influencer.nextLive,
  });

  return (
    <div className={'pb-6'}>
      <Influencer
        influencer={influencer}
        isInfluencerLive={isInfluencerLive}
        latestLiveTime={latestLiveTime}
        isStreamChosen={isStreamChosen}
        isVideoLive={isVideoLive}
        isGuest={isGuest}
        showRegistrationConfirmation={showRegistrationConfirmation}
        shopName={shopName}
        appStoreUrls={appStoreUrls}
        liveSaleJustEnded={liveSaleJustEnded}
        itemsInCart={cart?.itemCount || 0}
      >
        <div className={'mt-5 md:m-4 md:mt-6 max-w-screen-2xl 2xl:mx-auto'}>
          <LoadableLOTWConnector
            onLiveSaleEnded={handleLiveSaleEnded}
            streamId={streamIdToPlay}
            autoConnectToReplay={false}
            autoConnectToLive={isInfluencerLive}
          />
        </div>
      </Influencer>

      {!!replays.length && (
        <div className={'mt-4 px-4 pb-4 mx-auto max-w-screen-lg'}>
          <h2 className={'mb-0 font-semibold text-sm'}>Previous Lives</h2>
          <LiveReplays
            hasMore={hasMore}
            hideHeading={true}
            isError={hasReplaysError}
            isLoading={areReplaysLoading}
            onLoaderIsOnScreen={checkForMore}
            onReplayClick={onSelectReplay}
            replays={replays}
            useTouchNavigation={true}
          />
        </div>
      )}
    </div>
  );
};

export { InfluencerContainer };
